import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { of } from 'rxjs';

import { TokenStorageService } from '@/app/auth/services/token-storage.service';
import { AccountSecurityService } from '../services/account-security.service';
import * as AccountSecurityActions from './account-security.actions';
import { selectInitiateToken } from './account-security.reducer';
import * as DashboardActions from '@/app/dashboard/store/dashboard.actions';
import type {
  ResetGoogleAuthInitiateResponse,
  ResetPasswordResponse,
  VerifyGoogleAuthSetupResponse,
} from '../models/account-security.model';

@Injectable()
export class AccountSecurityEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(AccountSecurityService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly translate = inject(TranslateService);

  // ============================================
  // REGULAR GOOGLE AUTH SETUP FLOW
  // ============================================
  readonly googleAuthStartSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.googleAuthStartSetup),
      switchMap(() =>
        this.service.initiateGoogleAuthSetup().pipe(
          map(res =>
            AccountSecurityActions.googleAuthStartSetupSuccess({
              response: res?.data ?? {},
            })
          ),
          catchError(err =>
            of(
              AccountSecurityActions.googleAuthStartSetupFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly googleAuthVerifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.googleAuthVerifyOtp),
      switchMap(({ otp }) =>
        this.service.verifyGoogleAuthSetup({ otp }).pipe(
          map(res =>
            AccountSecurityActions.googleAuthVerifyOtpSuccess({
              response: res?.data ?? ({} as VerifyGoogleAuthSetupResponse),
            })
          ),
          catchError(err =>
            of(
              AccountSecurityActions.googleAuthVerifyOtpFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  // Refetch user profile after successful Google Auth activation
  readonly refetchUserAfterGoogleAuthSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.googleAuthVerifyOtpSuccess),
      map(() => DashboardActions.loadUser())
    )
  );

  readonly resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.resetPassword),
      switchMap(({ userId, payload }) =>
        this.service.resetPassword(userId, payload).pipe(
          map(res =>
            AccountSecurityActions.resetPasswordSuccess({
              response: res?.data ?? ({} as ResetPasswordResponse),
            })
          ),
          catchError(err =>
            of(
              AccountSecurityActions.resetPasswordFailure({
                error: this.normalizeError(err),
                errorCode: err?.error?.errorCode ?? null,
              })
            )
          )
        )
      )
    )
  );

  // ============================================
  // RESET GOOGLE AUTH FLOW
  // ============================================
  readonly resetGoogleAuthInitiate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.resetGoogleAuthInitiate),
      switchMap(() =>
        this.service.initiateResetGoogleAuthSetup().pipe(
          map(res =>
            AccountSecurityActions.resetGoogleAuthInitiateSuccess({
              response: res?.data ?? ({} as ResetGoogleAuthInitiateResponse),
            })
          ),
          catchError(err =>
            of(
              AccountSecurityActions.resetGoogleAuthInitiateFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly resetGoogleAuthResendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.resetGoogleAuthResendOtp),
      switchMap(() =>
        this.service.initiateResetGoogleAuthSetup().pipe(
          map(res =>
            AccountSecurityActions.resetGoogleAuthResendOtpSuccess({
              response: res?.data ?? ({} as ResetGoogleAuthInitiateResponse),
            })
          ),
          catchError(err =>
            of(
              AccountSecurityActions.resetGoogleAuthResendOtpFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly logoutAfterPasswordChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountSecurityActions.logoutAfterPasswordChange),
        tap(() => {
          // Clear tokens since server has invalidated them
          this.tokenStorage.clear();
          // Navigate to login page
          this.router.navigate(['/auth/login'], {
            queryParams: { message: 'password_changed' },
          });
        })
      ),
    { dispatch: false }
  );

  readonly resetGoogleAuthVerifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.resetGoogleAuthVerifyOtp),
      withLatestFrom(this.store.pipe(select(selectInitiateToken))),
      switchMap(([{ otp }, token]) => {
        if (!token) {
          return of(
            AccountSecurityActions.resetGoogleAuthVerifyOtpFailure({
              error: this.translate.instant(
                'accountSettings.security.errors.tokenNotFound'
              ),
            })
          );
        }
        return this.service.verifyOtp({ otpCode: otp }, token).pipe(
          switchMap(res => {
            const verifiedToken = res?.data?.token;
            if (!verifiedToken) {
              return of(
                AccountSecurityActions.resetGoogleAuthVerifyOtpFailure({
                  error: this.translate.instant(
                    'accountSettings.security.errors.tokenNotReceived'
                  ),
                })
              );
            }
            return of(
              AccountSecurityActions.resetGoogleAuthVerifyOtpSuccess({
                token: verifiedToken,
              }),
              AccountSecurityActions.resetGoogleAuthFetchQrCode({
                token: verifiedToken,
              })
            );
          }),
          catchError(err =>
            of(
              AccountSecurityActions.resetGoogleAuthVerifyOtpFailure({
                error: this.normalizeError(err),
              })
            )
          )
        );
      })
    )
  );

  readonly resetGoogleAuthFetchQrCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountSecurityActions.resetGoogleAuthFetchQrCode),
      switchMap(({ token }) => {
        return this.service.resetGoogleAuthSetup(token).pipe(
          map(res =>
            AccountSecurityActions.resetGoogleAuthFetchQrCodeSuccess({
              response: res?.data ?? {},
            })
          ),
          catchError(err =>
            of(
              AccountSecurityActions.resetGoogleAuthFetchQrCodeFailure({
                error: this.normalizeError(err),
              })
            )
          )
        );
      })
    )
  );
  private normalizeError(err: unknown): string {
    const error = err as {
      error?: { message?: string | string[] | { message?: string } };
      message?: string;
    };

    if (Array.isArray(error?.error?.message)) {
      return error.error.message.length > 0
        ? error.error.message[0]
        : this.translate.instant('accountSettings.security.errors.generic');
    }

    if (
      error?.error?.message &&
      typeof error.error.message === 'object' &&
      error.error.message.message
    ) {
      return error.error.message.message;
    }

    if (typeof error?.error?.message === 'string') {
      return error.error.message;
    }

    return (
      error?.message ||
      this.translate.instant('accountSettings.security.errors.generic')
    );
  }
}
